import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';

import { type Workflow } from '@blaze-shared/workflow-domain';

import { calculateAllowedActionsForNode } from './debug-step-actions';
import * as debuggerActions from './workflow-debugger.actions';
import { selectWfDebugInfo } from './workflow-debugger.selectors';

@Injectable({
  providedIn: 'root',
})
export class DebuggerFacade {
  private readonly store = inject(Store);

  readonly debugInfo$ = this.store.select(selectWfDebugInfo);

  readonly debuggingWorkflow$ = this.debugInfo$.pipe(
    map(({ executionId, workflow }) => {
      if (!executionId) {
        return null;
      }
      return workflow ?? null;
    }),
    distinctUntilChanged(),
  );

  readonly isDebugging$ = this.store.select(selectWfDebugInfo).pipe(
    map((di) => !!di.executionId),
    distinctUntilChanged(),
  );

  readonly pendingNodeId$ = this.debugInfo$.pipe(
    map((debugInfo) => {
      return debugInfo.pendingNodeId;
    }),
    distinctUntilChanged(),
  );

  readonly isBusy$ = this.debugInfo$.pipe(
    map((debugInfo) => debugInfo.busy),
    distinctUntilChanged(),
  );

  readonly canRunNext$ = this.debugInfo$.pipe(
    map((debugInfo) => !!debugInfo.queuedStepPayload && !debugInfo.busy),
    distinctUntilChanged(),
  );

  readonly pendingNode$ = this.debugInfo$.pipe(
    map(({ pendingNodeId, workflow }) => {
      if (!pendingNodeId || !workflow) {
        return;
      }
      return workflow.nodes.find((node) => node.id === pendingNodeId);
    }),
    distinctUntilChanged(),
  );

  readonly pendingNodeAllowedActions$ = combineLatest([
    this.pendingNode$,
    this.debuggingWorkflow$,
  ]).pipe(
    map(([pendingNode, workflow]) => {
      if (!pendingNode || !workflow) {
        return [];
      }
      return calculateAllowedActionsForNode(pendingNode.id, workflow, pendingNode.id);
    }),
    distinctUntilChanged(),
  );

  startDebugger(workflow: Workflow, debuggerInputs?: { [key: string]: any }): void {
    const workflowId = workflow.id;
    this.store.dispatch(
      debuggerActions.runWorkflowDebugger({
        workflowId,
        workflow,
        inputsData: debuggerInputs,
        workflowExecutionId: `${workflowId}_${crypto.randomUUID()}`,
      }),
    );
  }

  stepOver(): void {
    this.store.dispatch(debuggerActions.workflowDebuggerStepOver());
  }

  stepInto(): void {
    this.store.dispatch(debuggerActions.workflowDebuggerStepInto());
  }

  stepOutside(): void {
    this.store.dispatch(debuggerActions.workflowDebuggerStepOutside());
  }

  runIntoNode(nodeId: string): void {
    this.store.dispatch(debuggerActions.workflowDebuggerRunToNode({ nodeId }));
  }
  stopDebugger(): void {
    this.store.dispatch(debuggerActions.stopWorkflowDebugger());
  }
}
